import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { I18nController, t } from "../../i18n/index.ts";
import { icons } from "../icons.ts";

const COPIED_FOR_MS = 1500;
const ERROR_FOR_MS = 2000;

type CopyButtonStatus = "idle" | "copying" | "copied" | "error";

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!text) {
    return false;
  }

  // 1. 优先使用 Clipboard API（安全上下文下可用）
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // 非安全上下文（file:// iframe、http://）下不可用
  }

  // 2. Electron 环境：通过 postMessage 调用壳子的 clipboard API（兼容 macOS/Windows/Linux）
  try {
    window.parent.postMessage({ type: "clipboard-write", text }, "*");
    // postMessage 是异步的无法确认结果，但 Electron clipboard 基本不会失败
    return true;
  } catch {
    // 不在 iframe 中或 postMessage 失败
  }

  // 3. 回退：document.execCommand('copy')
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.cssText = "position:fixed;left:-9999px;top:-9999px;opacity:0;";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

@customElement("openclaw-chat-copy-button")
export class OpenClawChatCopyButton extends LitElement {
  @property({ attribute: false }) text = "";

  @state() private status: CopyButtonStatus = "idle";

  private readonly i18nController = new I18nController(this);
  private resetTimer: number | null = null;

  createRenderRoot() {
    return this;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.clearResetTimer();
  }

  private clearResetTimer() {
    if (this.resetTimer !== null) {
      window.clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
  }

  private scheduleReset(ms: number) {
    this.clearResetTimer();
    this.resetTimer = window.setTimeout(() => {
      this.resetTimer = null;
      this.status = "idle";
    }, ms);
  }

  private get label() {
    switch (this.status) {
      case "copied":
        return t("chat.copyButton.copied");
      case "error":
        return t("chat.copyButton.failed");
      default:
        return t("chat.copyButton.copy");
    }
  }

  private handleClick = async () => {
    if (this.status === "copying") {
      return;
    }

    this.clearResetTimer();
    this.status = "copying";

    const copied = await copyTextToClipboard(this.text);
    if (!this.isConnected) {
      return;
    }

    if (!copied) {
      this.status = "error";
      this.scheduleReset(ERROR_FOR_MS);
      return;
    }

    this.status = "copied";
    this.scheduleReset(COPIED_FOR_MS);
  };

  render() {
    const label = this.label;
    const isCopying = this.status === "copying";

    return html`
      <button
        class="chat-copy-btn"
        type="button"
        title=${label}
        aria-label=${label}
        aria-busy=${isCopying ? "true" : nothing}
        ?disabled=${isCopying}
        data-copying=${isCopying ? "1" : nothing}
        data-copied=${this.status === "copied" ? "1" : nothing}
        data-error=${this.status === "error" ? "1" : nothing}
        @click=${this.handleClick}
      >
        <span class="chat-copy-btn__icon" aria-hidden="true">
          <span class="chat-copy-btn__icon-copy">${icons.copy}</span>
          <span class="chat-copy-btn__icon-check">${icons.check}</span>
        </span>
        <span class="chat-copy-btn__label">${label}</span>
      </button>
    `;
  }
}

export function renderCopyAsMarkdownButton(markdown: string): TemplateResult {
  return html`<openclaw-chat-copy-button .text=${markdown}></openclaw-chat-copy-button>`;
}

declare global {
  interface HTMLElementTagNameMap {
    "openclaw-chat-copy-button": OpenClawChatCopyButton;
  }
}
